import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import classNames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { organizationRef } from '../../util/siteStructuredData';
import { isScrollingDisabled } from '../../ducks/ui.duck';
// Generic Unsplash sizing helpers (deterministic src/srcSet so the hero
// preload and the <img> stay byte-identical across SSR and client).
import { heroImageProps, unsplashVariant } from '../../util/unsplashImages';

import {
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  Bot,
  Building2,
  CalendarClock,
  Check,
  MessageCircle,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Wallet,
  Zap,
} from 'lucide-react';

import { H1, Page, NamedLink, ExternalLink, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import SectionLogoSlider from '../PageBuilder/SectionBuilder/SectionLogoSlider';
import SectionEarningsCalculator from '../PageBuilder/SectionBuilder/SectionEarningsCalculator';
import SectionZurichMap from '../PageBuilder/SectionBuilder/SectionZurichMap';
import ListSpaceCta from './ListSpaceCta';

import css from './SpaceOwnerLandingPage.module.css';

const FAQ_COUNT = 8;
const CALCULATOR_ANCHOR = 'earnings-calculator';

// The published cowork24 app in ChatGPT's directory.
const CHATGPT_APP_URL =
  'https://chatgpt.com/plugins/plugin_asdk_app_6a634c50a9a08191b8322f9404848551?q=cowork';

// cowork24's remote MCP server, published in the official MCP registry as
// `ch.cowork24/booking`. This is the address for every OTHER assistant (Claude
// et al.); the host speaks MCP only, so it is shown as copyable text.
const MCP_ENDPOINT = 'mcp.cowork24.ch/mcp';

// Hero / Open Graph imagery (Unsplash — CSP imgSrc allows images.unsplash.com
// only; plus.unsplash.com premium photos are blocked). A bright, high-end
// workspace whose coral seating harmonises with the brand accent. Decorative;
// hidden on phones so the H1 stays the mobile LCP.
const HERO_IMAGE_URL = 'https://images.unsplash.com/photo-1497366811353-6870744d04b2';
const OG_IMAGE_URL = `${HERO_IMAGE_URL}?w=1200&h=630&fit=crop&crop=center`;

// Thumbnails inside the illustrative ChatGPT conversation mockup.
const CHAT_THUMBS = [
  unsplashVariant('https://images.unsplash.com/photo-1527192491265-7e15c55b1ed2', 320, 200),
  unsplashVariant('https://images.unsplash.com/photo-1568992687947-868a62a9f521', 320, 200),
];

const TRUST_ITEMS = [
  { id: 'trust-free', Icon: BadgeCheck, labelId: 'SpaceOwnerLandingPage.trust1' },
  { id: 'trust-stripe', Icon: ShieldCheck, labelId: 'SpaceOwnerLandingPage.trust2' },
  { id: 'trust-control', Icon: SlidersHorizontal, labelId: 'SpaceOwnerLandingPage.trust3' },
  { id: 'trust-fast', Icon: Zap, labelId: 'SpaceOwnerLandingPage.trust4' },
];

// Design System v3: dimensional icon tiles are the one place depth lives, and
// only on marketing surfaces like this page. Red stays a sparse accent.
const BENEFITS = [
  { id: 'free', Icon: Wallet, tile: css.tileGreen, n: 1 },
  { id: 'flexible', Icon: CalendarClock, tile: css.tilePetrol, n: 2 },
  { id: 'secure', Icon: ShieldCheck, tile: css.tileRed, n: 3 },
  { id: 'control', Icon: SlidersHorizontal, tile: css.tileBlush, n: 4 },
];

const AI_POINTS = [
  { id: 'ai-chatgpt', Icon: MessageCircle, n: 1 },
  { id: 'ai-agents', Icon: Bot, n: 2 },
  { id: 'ai-auto', Icon: Zap, n: 3 },
];

const CheckIcon = props => (
  <svg
    className={props.className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/**
 * Landing page for coworking space owners at /p/space_owner.
 *
 * Standalone container (Page + LayoutSingleColumn — the AirportLoungeLandingPage
 * shape) instead of PageBuilder: this unlocks localized meta, schema.org blocks
 * merged into the Page @graph, an Open Graph image and the media-gated hero
 * preload that keeps the mobile LCP on the H1.
 */
export const SpaceOwnerLandingPageComponent = props => {
  const { scrollingDisabled = false } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();

  // Sticky CTA renders only after mount so SSR and the first client render
  // stay identical (React #418/#423).
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const [openFaqId, setOpenFaqId] = useState(null);
  const toggleFaq = id => setOpenFaqId(prev => (prev === id ? null : id));

  const marketplaceRootURL = config.marketplaceRootURL?.replace(/\/$/, '') || '';
  const canonicalPath = `${marketplaceRootURL}${createResourceLocatorString(
    'SpaceOwnerLandingPage',
    routeConfiguration,
    {},
    {}
  )}`;

  const pageTitle = intl.formatMessage({ id: 'SpaceOwnerLandingPage.metaTitle' });
  const pageDescription = intl.formatMessage({ id: 'SpaceOwnerLandingPage.metaDescription' });

  // Below 1024px the hero is a single column; above it the photo takes the 9fr
  // half of an 11fr/9fr grid (~45vw), so don't let the browser shop for a 60vw
  // candidate — the preload sits in the desktop LCP path.
  const heroImage = heroImageProps(HERO_IMAGE_URL, '(max-width: 1023px) 100vw, 45vw');

  const faqEntities = useMemo(
    () =>
      Array.from({ length: FAQ_COUNT }, (_, i) => i + 1).map(n => ({
        '@type': 'Question',
        name: intl.formatMessage({ id: `SpaceOwnerLandingPage.faq${n}Question` }),
        acceptedAnswer: {
          '@type': 'Answer',
          text: intl.formatMessage({ id: `SpaceOwnerLandingPage.faq${n}Answer` }),
        },
      })),
    [intl]
  );

  const schemaBlocks = useMemo(() => {
    const webPage = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${canonicalPath}#webpage`,
      url: canonicalPath,
      name: pageTitle,
      description: pageDescription,
      inLanguage: intl.locale,
      about: {
        '@type': 'Thing',
        name: intl.formatMessage({ id: 'SpaceOwnerLandingPage.aboutName' }),
      },
    };

    const faqPage = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${canonicalPath}#faq`,
      mainEntity: faqEntities,
    };

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: intl.formatMessage({ id: 'SpaceOwnerLandingPage.breadcrumbHome' }),
          item: `${marketplaceRootURL}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: intl.formatMessage({ id: 'SpaceOwnerLandingPage.breadcrumbCurrent' }),
          item: canonicalPath,
        },
      ],
    };

    const service = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${canonicalPath}#service`,
      name: intl.formatMessage({ id: 'SpaceOwnerLandingPage.aboutName' }),
      serviceType: intl.formatMessage({ id: 'SpaceOwnerLandingPage.serviceType' }),
      // A reference to the one Organization described on the home page, not a third copy of it.
      provider: organizationRef(marketplaceRootURL),
      areaServed: { '@type': 'Country', name: 'Switzerland' },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'CHF',
        description: intl.formatMessage({ id: 'SpaceOwnerLandingPage.offerDescription' }),
      },
    };

    return [webPage, faqPage, breadcrumb, service];
  }, [canonicalPath, faqEntities, intl, marketplaceRootURL, pageDescription, pageTitle]);

  const socialSharing = {
    title: pageTitle,
    description: pageDescription,
    images1200: [{ name: 'space-owner-open-graph', url: OG_IMAGE_URL, width: 1200, height: 630 }],
  };

  return (
    <Page
      className={css.root}
      title={pageTitle}
      description={pageDescription}
      scrollingDisabled={scrollingDisabled}
      schema={schemaBlocks}
      socialSharing={socialSharing}
    >
      <Helmet>
        {/* No crossorigin: plain <img> fetches use a non-CORS connection, and a
            CORS-flagged preconnect would never be reused for them. */}
        <link rel="preconnect" href="https://images.unsplash.com" />
        {/* Preload only where the hero photo actually renders (>=768px). On
            phones the photo is hidden and the H1 is the LCP, so preloading it
            there would just steal bandwidth from first paint. */}
        <link
          rel="preload"
          as="image"
          href={heroImage.src}
          imagesrcset={heroImage.srcSet}
          imagesizes={heroImage.sizes}
          media="(min-width: 768px)"
          fetchpriority="high"
        />
      </Helmet>

      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={
          // Wrapped so the mobile sticky CTA can't sit on top of the footer's
          // last rows (support and legal links) at maximum scroll.
          <div className={css.footerSpacer}>
            <FooterContainer />
          </div>
        }
        mainColumnClassName={css.layoutMain}
      >
        <div className={css.shell}>
          {/* ── Hero ─────────────────────────────────────────────────── */}
          {/* Every list below carries an explicit role="list": each is styled
              with `list-style: none`, and Safari/VoiceOver drops list semantics
              for those, so the item count would never be announced. */}
          <section className={css.hero} aria-labelledby="space-owner-hero-title">
            <div className={css.heroInner}>
              <div className={css.heroCopy}>
                <nav
                  className={css.breadcrumbs}
                  aria-label={intl.formatMessage({ id: 'SpaceOwnerLandingPage.breadcrumbAria' })}
                >
                  <ol className={css.breadcrumbList} role="list">
                    <li className={css.breadcrumbItem}>
                      <NamedLink name="LandingPage" className={css.breadcrumbLink}>
                        <FormattedMessage id="SpaceOwnerLandingPage.breadcrumbHome" />
                      </NamedLink>
                    </li>
                    <li className={css.breadcrumbItem} aria-current="page">
                      <span className={css.breadcrumbCurrent}>
                        <FormattedMessage id="SpaceOwnerLandingPage.breadcrumbCurrent" />
                      </span>
                    </li>
                  </ol>
                </nav>

                <span className={css.eyebrow}>
                  <Building2 size={15} strokeWidth={2} aria-hidden="true" />
                  <FormattedMessage id="SpaceOwnerLandingPage.eyebrow" />
                </span>

                <H1 id="space-owner-hero-title" className={css.heroTitle}>
                  <FormattedMessage id="SpaceOwnerLandingPage.heroTitle" />
                </H1>
                <p className={css.heroIntro}>
                  <FormattedMessage id="SpaceOwnerLandingPage.heroSubtitle" />
                </p>

                <div className={css.heroCtaRow}>
                  <ListSpaceCta ctaId="so-hero-list-cta" className={css.ctaPrimary}>
                    <FormattedMessage id="SpaceOwnerLandingPage.heroCta" />
                  </ListSpaceCta>
                  <a
                    id="so-hero-calc-cta"
                    href={`#${CALCULATOR_ANCHOR}`}
                    className={css.ctaSecondary}
                    data-track-cta="so-hero-calc-cta"
                  >
                    <FormattedMessage id="SpaceOwnerLandingPage.heroCtaSecondary" />
                  </a>
                </div>
                <p className={css.heroHint}>
                  <FormattedMessage id="SpaceOwnerLandingPage.heroCtaHint" />
                </p>

                <ul className={css.heroBadges} role="list">
                  {[1, 2, 3].map(n => (
                    <li key={n} className={css.heroBadge}>
                      <CheckIcon className={css.heroBadgeIcon} />
                      <FormattedMessage id={`SpaceOwnerLandingPage.heroBadge${n}`} />
                    </li>
                  ))}
                </ul>
              </div>

              <div className={css.heroVisual} aria-hidden="true">
                {/* Lazy + display:none on phones → no fetch there; the >=768px
                    preload above drives a fast desktop LCP from cache. */}
                <img
                  {...heroImage}
                  alt=""
                  className={css.heroPhoto}
                  loading="lazy"
                  decoding="async"
                />
                <span className={css.heroAiChip}>
                  <Sparkles size={14} strokeWidth={2} />
                  <FormattedMessage id="SpaceOwnerLandingPage.heroAiChip" />
                </span>
                <div className={css.heroNotif}>
                  <span className={css.heroNotifIcon}>
                    <Check size={16} strokeWidth={2.5} />
                  </span>
                  <span className={css.heroNotifText}>
                    <strong>
                      <FormattedMessage id="SpaceOwnerLandingPage.heroNotifTitle" />
                    </strong>
                    <span>
                      <FormattedMessage id="SpaceOwnerLandingPage.heroNotifMeta" />
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* ── Trust strip ──────────────────────────────────────────── */}
          <section className={css.trustSection}>
            <div className={css.trustInner}>
              {TRUST_ITEMS.map(({ id, Icon, labelId }) => (
                <div key={id} className={css.trustItem}>
                  <span className={css.trustIcon}>
                    <Icon size={18} strokeWidth={1.75} aria-hidden="true" />
                  </span>
                  <FormattedMessage id={labelId} />
                </div>
              ))}
            </div>
          </section>

          {/* ── Social proof: real spaces already on cowork24 ────────── */}
          <SectionLogoSlider
            sectionId="social-proof"
            landingSurface="muted"
            eyebrowId="SpaceOwnerLandingPage.logosEyebrow"
            titleId="SpaceOwnerLandingPage.logosTitle"
            subtitleId="SpaceOwnerLandingPage.logosSubtitle"
          />

          {/* ── Earnings calculator ──────────────────────────────────── */}
          <SectionEarningsCalculator sectionId={CALCULATOR_ANCHOR} />

          {/* ── Benefits ─────────────────────────────────────────────── */}
          <section className={css.benefitsSection} aria-labelledby="space-owner-benefits-title">
            <div className={css.inner}>
              <h2 id="space-owner-benefits-title" className={css.sectionTitle}>
                <FormattedMessage id="SpaceOwnerLandingPage.benefitsTitle" />
              </h2>
              <p className={css.sectionSubtitle}>
                <FormattedMessage id="SpaceOwnerLandingPage.benefitsSubtitle" />
              </p>
              <div className={css.benefitsGrid}>
                {BENEFITS.map(({ id, Icon, tile, n }) => (
                  <article key={id} className={css.benefitCard}>
                    <span className={classNames(css.iconTile, tile)}>
                      <Icon size={22} strokeWidth={1.75} aria-hidden="true" />
                    </span>
                    <h3 className={css.benefitTitle}>
                      <FormattedMessage id={`SpaceOwnerLandingPage.benefit${n}Title`} />
                    </h3>
                    <p className={css.benefitDesc}>
                      <FormattedMessage id={`SpaceOwnerLandingPage.benefit${n}Desc`} />
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* ── AI reach: found and bookable via ChatGPT & AI agents ─── */}
          <section className={css.aiSection} aria-labelledby="space-owner-ai-title">
            <div className={classNames(css.inner, css.aiInner)}>
              <div className={css.aiCopy}>
                <span className={css.aiEyebrow}>
                  <Sparkles size={14} strokeWidth={2} aria-hidden="true" />
                  <FormattedMessage id="SpaceOwnerLandingPage.aiEyebrow" />
                </span>
                <h2 id="space-owner-ai-title" className={css.aiTitle}>
                  <FormattedMessage id="SpaceOwnerLandingPage.aiTitle" />
                </h2>
                <p className={css.aiSubtitle}>
                  <FormattedMessage id="SpaceOwnerLandingPage.aiSubtitle" />
                </p>
                <ul className={css.aiPoints} role="list">
                  {AI_POINTS.map(({ id, Icon, n }) => (
                    <li key={id} className={css.aiPoint}>
                      <span className={css.aiPointIcon}>
                        <Icon size={18} strokeWidth={1.75} aria-hidden="true" />
                      </span>
                      <span className={css.aiPointText}>
                        <strong>
                          <FormattedMessage id={`SpaceOwnerLandingPage.aiPoint${n}Title`} />
                        </strong>
                        <FormattedMessage id={`SpaceOwnerLandingPage.aiPoint${n}Desc`} />
                      </span>
                    </li>
                  ))}
                </ul>
                {/* The "list your space" CTA that used to sit here was one of six identical ones
                    on this page. A repeated primary action does not add a way in, it just makes
                    every section look like the end of the page — the visitor is still reading
                    about the ChatGPT connector, and the hero, the calculator, the closing block
                    and the sticky mobile bar all still offer it. What is left here is the one
                    link this section alone can give: the connector itself. */}
                <div className={css.aiCtaRow}>
                  {/* Outbound link → the global cta_click listener picks this up
                      automatically (util/clickTracking.js treats it as outbound). */}
                  <ExternalLink
                    id="so-ai-chatgpt-link"
                    href={CHATGPT_APP_URL}
                    className={css.aiChatgptLink}
                  >
                    <MessageCircle size={16} strokeWidth={2} aria-hidden="true" />
                    <FormattedMessage id="SpaceOwnerLandingPage.aiConnectorCta" />
                    <ArrowUpRight size={15} strokeWidth={2} aria-hidden="true" />
                  </ExternalLink>
                </div>
                {/* Other assistants connect by URL, so the MCP endpoint is
                    shown verbatim and selectable rather than linked. */}
                <p className={css.aiConnectorHint}>
                  <FormattedMessage id="SpaceOwnerLandingPage.aiConnectorHint" />
                  <code className={css.aiConnectorUrl}>{MCP_ENDPOINT}</code>
                </p>
              </div>

              {/* Illustrative product mockup of a ChatGPT conversation using
                  the cowork24 connector. Decorative for assistive tech. */}
              <div className={css.chatWindow} aria-hidden="true">
                <div className={css.chatHeader}>
                  <span className={css.chatDots}>
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className={css.chatTitle}>ChatGPT</span>
                  <span className={css.chatAppBadge}>
                    <Sparkles size={11} strokeWidth={2} />
                    cowork24
                  </span>
                </div>
                <div className={css.chatBody}>
                  <div className={css.chatUserMsg}>
                    <FormattedMessage id="SpaceOwnerLandingPage.aiChatUser" />
                  </div>
                  <div className={css.chatAssistant}>
                    <span className={css.chatAppChip}>
                      <Bot size={13} strokeWidth={2} />
                      cowork24
                    </span>
                    <p className={css.chatIntro}>
                      <FormattedMessage id="SpaceOwnerLandingPage.aiChatIntro" />
                    </p>
                    <div className={css.chatCards}>
                      {[1, 2].map(n => (
                        <div key={n} className={css.chatCard}>
                          <img
                            src={CHAT_THUMBS[n - 1]}
                            alt=""
                            className={css.chatCardImg}
                            loading="lazy"
                            decoding="async"
                            width="320"
                            height="200"
                          />
                          <div className={css.chatCardBody}>
                            <span className={css.chatCardName}>
                              <FormattedMessage id={`SpaceOwnerLandingPage.aiChatCard${n}Name`} />
                            </span>
                            <span className={css.chatCardMeta}>
                              <FormattedMessage id={`SpaceOwnerLandingPage.aiChatCard${n}Meta`} />
                            </span>
                            <span className={css.chatCardAvail}>
                              <span className={css.chatAvailDot} />
                              <FormattedMessage id="SpaceOwnerLandingPage.aiChatAvailable" />
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <span className={css.chatAction}>
                      <FormattedMessage id="SpaceOwnerLandingPage.aiChatAction" />
                      <ArrowRight size={14} strokeWidth={2} />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── How it works ─────────────────────────────────────────── */}
          <section className={css.stepsSection} aria-labelledby="space-owner-steps-title">
            <div className={css.inner}>
              <h2 id="space-owner-steps-title" className={css.sectionTitle}>
                <FormattedMessage id="SpaceOwnerLandingPage.howItWorksTitle" />
              </h2>
              <p className={css.sectionSubtitle}>
                <FormattedMessage id="SpaceOwnerLandingPage.howItWorksSubtitle" />
              </p>
              <ol className={css.stepsGrid} role="list">
                {[1, 2, 3].map(n => (
                  <li key={n} className={css.step}>
                    <span className={css.stepNumber}>{n}</span>
                    <h3 className={css.stepTitle}>
                      <FormattedMessage id={`SpaceOwnerLandingPage.step${n}Title`} />
                    </h3>
                    <p className={css.stepDesc}>
                      <FormattedMessage id={`SpaceOwnerLandingPage.step${n}Desc`} />
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* ── Local proof: Zürich map with real spaces ─────────────── */}
          <SectionZurichMap
            sectionId="zurich-map"
            landingSurface="muted"
            eyebrowId="SpaceOwnerLandingPage.mapEyebrow"
            titleId="SpaceOwnerLandingPage.mapTitle"
            subtitleId="SpaceOwnerLandingPage.mapSubtitle"
          />

          {/* ── FAQ ──────────────────────────────────────────────────── */}
          <section className={css.faqSection} aria-labelledby="space-owner-faq-title">
            <div className={css.faqInner}>
              <h2 id="space-owner-faq-title" className={css.sectionTitle}>
                <FormattedMessage id="SpaceOwnerLandingPage.faqTitle" />
              </h2>
              <p className={css.sectionSubtitle}>
                <FormattedMessage id="SpaceOwnerLandingPage.faqSubtitle" />
              </p>
              <dl className={css.faqList}>
                {Array.from({ length: FAQ_COUNT }, (_, i) => i + 1).map(n => {
                  const id = `ownerFaq${n}`;
                  const isOpen = openFaqId === id;
                  return (
                    <div key={id} className={css.faqItem}>
                      <dt>
                        <button
                          type="button"
                          className={classNames(css.faqQuestion, {
                            [css.faqQuestionOpen]: isOpen,
                          })}
                          onClick={() => toggleFaq(id)}
                          aria-expanded={isOpen}
                          aria-controls={`${id}-answer`}
                          id={`${id}-question`}
                        >
                          <FormattedMessage id={`SpaceOwnerLandingPage.faq${n}Question`} />
                          <span className={css.faqIcon} aria-hidden="true">
                            {isOpen ? '−' : '+'}
                          </span>
                        </button>
                      </dt>
                      <dd
                        id={`${id}-answer`}
                        className={classNames(css.faqAnswerWrap, {
                          [css.faqAnswerWrapOpen]: isOpen,
                        })}
                        aria-labelledby={`${id}-question`}
                      >
                        <div className={css.faqAnswer}>
                          <FormattedMessage id={`SpaceOwnerLandingPage.faq${n}Answer`} />
                        </div>
                      </dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          </section>

          {/* ── Final CTA ────────────────────────────────────────────── */}
          <section className={css.finalSection} aria-labelledby="space-owner-final-title">
            <div className={css.finalInner}>
              <h2 id="space-owner-final-title" className={css.finalTitle}>
                <FormattedMessage id="SpaceOwnerLandingPage.finalCtaTitle" />
              </h2>
              <p className={css.finalSubtitle}>
                <FormattedMessage id="SpaceOwnerLandingPage.finalCtaSubtitle" />
              </p>
              <ListSpaceCta ctaId="so-final-list-cta" className={css.ctaPrimaryLarge}>
                <FormattedMessage id="SpaceOwnerLandingPage.finalCtaButton" />
              </ListSpaceCta>
              <ul className={css.finalPoints} role="list">
                {[1, 2, 3].map(n => (
                  <li key={n} className={css.finalPoint}>
                    <CheckIcon className={css.finalPointIcon} />
                    <FormattedMessage id={`SpaceOwnerLandingPage.finalCtaPoint${n}`} />
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>

      {/* ── Mobile sticky CTA ──────────────────────────────────────── */}
      {mounted ? (
        <div
          className={css.stickyCta}
          role="region"
          aria-label={intl.formatMessage({ id: 'SpaceOwnerLandingPage.stickyCtaButton' })}
        >
          <span className={css.stickyCtaText}>
            <FormattedMessage id="SpaceOwnerLandingPage.stickyCtaText" />
          </span>
          <ListSpaceCta ctaId="so-sticky-list-cta" className={css.stickyCtaButton}>
            <FormattedMessage id="SpaceOwnerLandingPage.stickyCtaButton" />
          </ListSpaceCta>
        </div>
      ) : null}
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const SpaceOwnerLandingPage = compose(connect(mapStateToProps))(SpaceOwnerLandingPageComponent);

export default SpaceOwnerLandingPage;
